import Papa from "papaparse";
import React, { useState } from "react";
import { Form, ProgressBar, Row, Col, Container } from "react-bootstrap";
import FileUpload from "./FileUpload";
import UploadColumnMapping from "./UploadColumnMapping";
import { CsvImportDelimiterOptions } from "../../../static-data/DropdownOptions";
import { importTestCase } from "../../../modules/entity-details";
import Modal from "../Modal";
import { Alert, Button } from "@mui/material";
import SetDefaultsValues from "./SetDefaultsValues";
import sampleCsv from "../../../assets/sample_testcases.csv";
import { useNotification } from "../../../hooks/NotificationContext";
import { useUser } from "../../../context/UserContext";

const steps = ["Upload File", "Set values", "Map fields", "Import data"];

const FileImportComponent = ({
  show,
  handleClose,
  fetchTestCases,
  projectId,
  fetchTestCaseData,
}) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [delimiter, setDelimiter] = useState(',');
  const [csvData, setCsvData] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addNotification } = useNotification();
  const [responseSuccess, setResponseSuccess] = useState(null);
  const [backendMessage, setBackendMessage] = useState("");
  const { currentUser } = useUser();

  const initialValues = {
    testSuites: [],
  };
  const [formValues, setFormValues] = useState(initialValues);

  const handleCloseModal = () => {
    setStep(1);
    setFile(null);
    setCsvData([]);
    setColumnMapping({});
    handleClose();
    setFormValues(initialValues);
    setError(null);
  };

  const handleFileUpload = (file) => {
    setFile(file);
    setColumnMapping({});
    setCsvData([]);
  };

  const handleParseCSV = () => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const csvText = event.target.result;
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          delimiter: delimiter,
          complete: (results) => {
            console.log("PARSE RESULTS", results);
            if (results.errors && results.errors.length > 0) {
              setError(`Error parsing CSV: ${results.errors[0].message}`);
            } else {
              setCsvData(results.data);
              setStep(2);
              setError(null);
            }
          },
          error: (err) => {
            setError("Error parsing CSV. Please check the file format and delimiter.");
          },
        });
      };
        reader.readAsText(file, 'UTF-8');
    } else {
      setError("Please upload a file first.");
    }
  };
  const handleMappingChange = (e) => {
    const { name, value } = e.target;
    const updatedMapping = { ...columnMapping };
    if (value === "name") {
      setError(null);
    }
    if (value === "") {
      delete updatedMapping[name];
    } else {
      updatedMapping[name] = value;
    }
    setColumnMapping(updatedMapping);
    setError(null);
  };

  const handleNextStep = () => {
    if (step === 1) {
      handleParseCSV();
      return;
    }

    if (step === 2) {
      setError(null);
      setStep(3);
      return;
    }
    if (step === 3) {
      const mappedValues = Object.values(columnMapping);

      const hasSummary = mappedValues.includes("summary");
      if (!hasSummary) {
        setError('You must map at least one CSV field to the "Description" field.');
        return;
      }

      const hasStep = mappedValues.includes("step");
      const hasTestData = mappedValues.includes("testData");
      const hasExpectedResult = mappedValues.includes("expectedResult");
      const hasUri = mappedValues.includes("uri");

      if ((hasTestData || hasExpectedResult || hasUri) && !hasStep) {
        setError(`You must map "Step Description" if you map "Test Data", "Expected Result or "URI".`);
        return;
      }

      const hasIsAutomated = mappedValues.includes("isAutomated");
      const hasAutomationMethod = mappedValues.includes("automationMethod");
      const hasAutomatedBy = mappedValues.includes("automatedBy");
      const hasAutomationCountry = mappedValues.includes("automationCountry");
      const hasCountry = mappedValues.includes("country");

      if ((hasAutomationMethod || hasAutomatedBy || hasAutomationCountry) && !hasIsAutomated) {
        setError(`"Is Automated" must be mapped if "Automation Method" or "Automated By" or "Automated for country"is mapped.`);
        return;
      }

      if (hasIsAutomated && !hasAutomationMethod) {
        setError(`"Automation Method" is required when using "Is Automated" mapping.`);
        return;
      }

      if (hasAutomationCountry && !hasCountry) {
        setError(`"Country" must be mapped if you map "Automation Country".`);
        return;
      }
      const moduleCount = mappedValues.filter((v) => v === 'module').length;
      const labelCount = mappedValues.filter((v) => v === 'labels').length;
      const countryCount = mappedValues.filter((v) => v === 'country').length;
      const jiraIssuesCount = mappedValues.filter((v) => v === 'jiraIssues').length;

      if (moduleCount > 1 || labelCount > 1 || jiraIssuesCount > 1 || countryCount > 1) {
        setError('Module, Labels, Country and Jira Issues must each be mapped only once.');
        return;
      }
      const hasCategoryOrSubCategory =
        mappedValues.includes("notAutomatedCategory") ||
        mappedValues.includes("notAutomatedSubCategory") ||
        mappedValues.includes("reasonForNotAutomated");
      const hasIsAutomationNeeded = mappedValues.includes("isAutomationNeeded");

      if (hasCategoryOrSubCategory && !hasIsAutomationNeeded) {
        setError(`"Candidate for Automation" must be mapped if "Not Automated Category", "Not Automated Sub Category" or "Reason For Not Automating" is mapped.`);
        return;
      }

      setError(null);
      setStep(4);
    }
  };

  const handleRetry = () => {
    setStep(1);
    setColumnMapping({});
    setFormValues(initialValues);
    setError(null);
  };

  const handleBackStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
    setError(null);
  };

  const handleFinalSubmit = async () => {
    if (!csvData || !csvData.length || typeof csvData[0] !== "object") {
      setError("CSV file is empty or not properly formatted.");
      setStep(1);
      return;
    }
    setLoading(true);
    setStep(4);
    try {
      const summaryField = Object.keys(columnMapping).find(
        (key) => columnMapping[key] === "summary"
      );
      const stepField = Object.keys(columnMapping).find(
        (key) => columnMapping[key] === "step"
      );
      const testDataField = Object.keys(columnMapping).find(
        (key) => columnMapping[key] === "testData"
      );
      const expectedResultField = Object.keys(columnMapping).find(
        (key) => columnMapping[key] === "expectedResult"
      );
      const uriField = Object.keys(columnMapping).find(
        (key) => columnMapping[key] === "uri"
      );
      const moduleField = Object.keys(columnMapping).find(
        (key) => columnMapping[key] === 'module'
      );
      const labelsField = Object.keys(columnMapping).find(
        (key) => columnMapping[key] === 'labels'
      );
      const countryField = Object.keys(columnMapping).find(
      (key) => columnMapping[key] === 'country'
      );
     
      const automationCountryField = Object.keys(columnMapping).find(
      (key) => columnMapping[key] === 'automationCountry'
      );

      const jiraIssuesField = Object.keys(columnMapping).find(
        (key) => columnMapping[key] === 'jiraIssues'
      );

      const weblinkUrlField = Object.keys(columnMapping).find(
        (key) => columnMapping[key] === 'weblinkUrl'
      );
      const weblinkDescriptionField = Object.keys(columnMapping).find(
        (key) => columnMapping[key] === 'weblinkDescription'
      );

      const reasonForNotAutomatedField = Object.keys(columnMapping).find(
        (key) => columnMapping[key] === "reasonForNotAutomated"
      );
      const notAutomatedCategoryField = Object.keys(columnMapping).find(
        (key) => columnMapping[key] === "notAutomatedCategory"
      );
      const notAutomatedSubCategoryField = Object.keys(columnMapping).find(
        (key) => columnMapping[key] === "notAutomatedSubCategory"
      );

      if (!summaryField) throw new Error("Missing column mapping for 'Description' (summary).");

      const testCases = [];
      let currentTestCase = null;

      for (let i = 0; i < csvData.length; i++) {
        const rowNum = i + 2;
        const row = csvData[i];

        const rawSummary = row[summaryField];
        const summary = rawSummary ? rawSummary.toString().trim() : "";

        if (summary !== "") {
          const mappedRow = {
            summary: summary.slice(0, 255),
            steps: [],
            weblinks: [],
            technologyLayer: formValues.technologyLayer,
            testSuites: formValues.testSuites,
          };

          for (const [csvField, gainssField] of Object.entries(columnMapping)) {
            if (['step',
                'testData',
                'expectedResult',
                'uri',
                'module',
                'labels',
                'country',
                'automationCountry',
                'jiraIssues',
                'reasonForNotAutomated',  
                'notAutomatedCategory',    
                'notAutomatedSubCategory',
                'weblinkUrl',
                'weblinkDescription'
              ].includes(gainssField)
            )
              continue;

            let value = row[csvField]?.toString().trim();
            if (!value) continue;

            if (["summary", "owner"].includes(gainssField)) {
              value = value.slice(0, 255);
            } else if (["objective", "preCondition"].includes(gainssField)) {
              value = value.slice(0, 500);
            } else if (gainssField === "automationMethod" && value.length > 100) {
              throw new Error(`"Automation Method" exceeds 100 characters at row ${rowNum}`);
            }

            if (gainssField === "isAutomated") {
              const raw = value.toLowerCase();
              if (!["yes", "no"].includes(raw)) {
                throw new Error(`Invalid "Is Automated" value at row ${rowNum}. Use "Yes" or "No".`);
              }
              mappedRow.isAutomated = raw === "yes" ? "Yes" : "No";
              if (raw === "yes") {
                mappedRow.isAutomationNeeded = "Yes";
              }
            } else if (gainssField === "automatedBy") {
              mappedRow.automatedBy = value || currentUser?.name || "";
            } else if (gainssField === "automationMethod") {
              mappedRow.automationMethod = value;
            } else if (gainssField === "isAutomationNeeded") {
              const raw = value.toLowerCase();
              if (!["yes", "no"].includes(raw)) {
                throw new Error(`Invalid value for candidate for automation at row ${rowNum}. Use "Yes" or "No".`);
              }
              mappedRow.isAutomationNeeded = raw === "yes" ? "Yes" : "No";
            } else {
              mappedRow[gainssField] = value;
            }
          }
          if (moduleField) {
            const moduleValue = row[moduleField]?.toString().trim();
            if (moduleValue) mappedRow.module = moduleValue.slice(0, 255);
          }

          if (labelsField && row[labelsField]) {
            const labelsArray = row[labelsField]
              .toString()
              .trim()
              .split(',')
              .map((label) => label.trim())
              .filter((label) => label.length > 0)
              .map((label) => label.slice(0, 1000));

            if (labelsArray.length > 0) {
              mappedRow.labels = labelsArray;
            }
          }
          if (countryField && row[countryField]) {
            const countryArray = row[countryField]
              .toString()
              .trim()
           .split(',')
              .map((country) => country.trim())
              .filter((country) => country.length > 0)
              .map((country) => country.slice(0, 255));

            if (countryArray.length > 0) {
              mappedRow.country = countryArray;
            }
          }

          if (automationCountryField && row[automationCountryField]) {
            const automatedCountryArray = row[automationCountryField]
              .toString()
              .trim()
         .split(',')
              .map((country) => country.trim())
              .filter((country) => country.length > 0)
              .map((country) => country.slice(0, 255));

        if (mappedRow.isAutomated === 'Yes' && automatedCountryArray.length > 0) {
              mappedRow.automationCountry = automatedCountryArray;
            }
          }


          if (jiraIssuesField && row[jiraIssuesField]) {
            const jiraIssuesArray = row[jiraIssuesField]
              .toString()
              .trim()
                    .split(',')
              .map((key) => key.trim())
              .filter((key) => key.length > 0)
              .map((key) => key.slice(0, 255));

            if (jiraIssuesArray.length > 0) {
              mappedRow.jiraIssues = jiraIssuesArray;
            }
          }

          if (reasonForNotAutomatedField && row[reasonForNotAutomatedField]) {
            const val = row[reasonForNotAutomatedField].toString().trim();
            if (val) mappedRow.reasonForNotAutomated = val;
          }

          if (notAutomatedCategoryField && row[notAutomatedCategoryField]) {
            const val = row[notAutomatedCategoryField].toString().trim();
            if (val) mappedRow.notAutomatedCategory = val;
          }

          if (notAutomatedSubCategoryField && row[notAutomatedSubCategoryField]) {
            const val = row[notAutomatedSubCategoryField].toString().trim();
            if (val) mappedRow.notAutomatedSubCategory = val;
          }

          if (mappedRow.isAutomated === 'Yes') {
            if (!mappedRow.automationMethod) {
              throw new Error(`"Automation Method" is required when 'Is Automated' is 'Yes' at row ${rowNum}`);
            }
            if (!mappedRow.automatedBy) {
              mappedRow.automatedBy = currentUser?.name || "";
            }
          } else {
            mappedRow.isAutomated = "No";
            delete mappedRow.automationMethod;
            delete mappedRow.automatedBy;
          }
         if (
            mappedRow.isAutomationNeeded === "Yes" &&
            mappedRow.isAutomated === "No" &&
            mappedRow.automationStatus !== "To Be Planned" &&
            !mappedRow.notAutomatedCategory &&
            !mappedRow.notAutomatedSubCategory
          ) {
            throw new Error(
              `Not Automated Category and Subcategory are required when "Candidate for Automation" is "Yes" and "Is Automated" is "No" at row ${rowNum}`
            );
          }

          currentTestCase = mappedRow;
          testCases.push(currentTestCase);
        }

        if (!currentTestCase) {
          const hasStep = stepField && row[stepField]?.toString().trim();
          const hasTestData = testDataField && row[testDataField]?.toString().trim();
          const hasExpected = expectedResultField && row[expectedResultField]?.toString().trim();
          const hasUri = uriField && row[uriField]?.toString().trim();

          if (hasStep || hasTestData || hasExpected || hasUri) {
            throw new Error(`No test case to assign step data at row ${rowNum}`);
          }
          continue;
        }

        const stepDescription = stepField ? row[stepField]?.toString().trim() : "";
        const testDataValue = testDataField ? row[testDataField]?.toString().trim() : "";
        const expectedResultValue = expectedResultField ? row[expectedResultField]?.toString().trim() : "";
        const uriValue = uriField ? row[uriField]?.toString().trim() : "";
        if (formValues?.technologyLayer !== "API/Integration" && uriValue) {
          throw new Error(`URI field can only be used with the 'API/Integration' technology layer. Either remove the URI field from mapping or change the technology layer`);
        }

        if (!stepDescription && (testDataValue || expectedResultValue || uriValue)) {
          throw new Error(`Step description is missing but other test step field are present at row ${rowNum}`);
        }

        if (stepDescription) {
          currentTestCase.steps.push({
            step: stepDescription.slice(0, 2000),
            testData: testDataValue ? testDataValue.slice(0, 2000) : "",
            expectedResult: expectedResultValue ? expectedResultValue.slice(0, 2000) : "",
            uri: uriValue ? uriValue.slice(0, 250) : "",
          });
        }

        const weblinkUrlValue = weblinkUrlField ? row[weblinkUrlField]?.toString().trim() : "";
        const weblinkDescValue = weblinkDescriptionField ? row[weblinkDescriptionField]?.toString().trim() : "";
        if (weblinkUrlValue) {
          currentTestCase.weblinks.push({
            url: weblinkUrlValue.slice(0, 2000),
            description: weblinkDescValue ? weblinkDescValue.slice(0, 2000) : "",
          });
        }
      }

      if (!testCases.length) {
        throw new Error("No valid test cases found in the CSV.");
      }

      const response = await importTestCase({ testcases: testCases }, projectId);
      setResponseSuccess(response?.half_done ?? null);
      setBackendMessage(response?.message ?? "");
      if (response?.half_done === true) {
        addNotification("success", response?.message || "Import queued for processing");
      } else {
        addNotification("success", "Test cases imported successfully!");
        fetchTestCases();
      }
      fetchTestCaseData();
     
    } catch (error) {
      setError("Failed to import test cases." + error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStepTracker = () => {
    return (
      <Row className="mb-4">
        {steps.map((stepLabel, index) => (
          <Col key={index} className="text-center">
            <div
              style={{
                borderRadius: "50%",
                backgroundColor: step >= index + 1 ? "#007bff" : "#cccccc",
                color: "white",
                display: "inline-block",
                width: "30px",
                height: "30px",
                textAlign: "center",
                lineHeight: "30px",
              }}
            >
              {index + 1}
            </div>
            <div>{stepLabel}</div>
          </Col>
        ))}
      </Row>
    );
  };

  const isButtonDisabled = 
  formValues.testSuites.length === 0;

  const generateModalFooter = () => {
    return (
      <>
      {step > 1 &&  (
        <Button variant="outlined" color="secondary" onClick={handleBackStep} disabled={step === 4 && !error}>
            Back
          </Button>
        )}
        {step === 1 && (
          <Button
            color="primary"
            variant="contained"
            onClick={handleNextStep}
            disabled={!file}
          >
            Next
          </Button>
        )}
        {step === 2 && (
          <Button
            color="primary"
            variant="contained"
            onClick={handleNextStep}
            disabled={isButtonDisabled}
          >
            Next
          </Button>
        )}
        {step === 3 && (
          <Button
            variant="contained"
            color="success"
            onClick={handleFinalSubmit}
            title={isButtonDisabled ? "Please select all mandatory fields" : "Import test cases"}
            disabled={Object.keys(columnMapping).length === 0}
          >
            Import
          </Button>
        )}
        {step === 4 && (
          <>
        {error &&
              <Button
                variant="contained"
                color="secondary"
                onClick={handleRetry}
                disabled={Object.keys(columnMapping).length === 0 || loading}
              >
                Retry
              </Button>
        }
            <Button
              variant="contained"
              color="success"
              onClick={handleCloseModal}
            disabled={Object.keys(columnMapping).length === 0 || loading}>
              Close
            </Button>
          </>
        )}
      </>
    );
  };
  return (
    <Modal
      open={show}
      handleClose={handleCloseModal}
      modalTitle="Import from CSV"
      action={generateModalFooter}
    >
      {renderStepTracker()}
      {step === 1 && (
        <>
          <Container fluid>
            <Row>
              <Col className="py-3 px-0">
                <a href={sampleCsv} download>
                  Click to download sample format
                </a>
              </Col>
            </Row>
          
          </Container>
          <Form>
            <FileUpload
              onUpload={handleFileUpload}
              enableDragAndDrop={true}
              file={file}
              setFile={setFile}
            />
            <Form.Group controlId="formDelimiter" style={{ width: "180px" }}>
              <Form.Label>CSV Delimiter</Form.Label>
              <Form.Select value={delimiter} onChange={(e) => setDelimiter(e.target.value)}>
                {CsvImportDelimiterOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </>
      )}
      {step === 2 && (
        <>
          {error && <Alert severity="error">{error}</Alert>}
        <SetDefaultsValues setFormValues={setFormValues} formValues={formValues} />
        </>
      )}
      {step === 3 && (
        <>
          <Alert severity="info" className="mb-2">
            The <strong>status</strong> and <strong>priority</strong> fields
            will be set to default values automatically.
            <br />
            <strong>Module</strong> and <strong>Labels</strong> will be created
            if they do not exist in the project.
            <br />
            <strong>URI</strong> field is imported only for API/Integration
            technology layer.
          </Alert>
          <UploadColumnMapping
            csvData={csvData}
            columnMapping={columnMapping}
            handleMappingChange={handleMappingChange}
            hasAutomationFields={true}
          />
           {error && <Alert severity="error" className="mt-3">{error}</Alert>}
        </>
      )}
      {step === 4 &&
        (loading ? (
          <>
            <p className="mb-2 text-center">
              Test cases are being imported. Please wait...
            </p>
            <ProgressBar
              striped
              animated
              now={100}
              style={{ width: "80%", margin: "0 auto"}}
            />
          </>
        ) : error ? (
          <Alert severity="error" className="my-3">
            {error}
          </Alert>
        ) : responseSuccess === true ? (
          <div className="text-center text-primary my-3">
            <h4>Import in Review</h4>
            <h5>{backendMessage}</h5>
          </div>
        ) : (
          <div className="text-center text-success my-3">
            <h4>Import Successful!</h4>
            <h5>Your test cases have been added successfully.</h5>
          </div>
        ))}
    </Modal>
  );
};

export default FileImportComponent;
